import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { ConfirmationModalShell } from '@/shared/components/confirmation'
import { confirmationBackdropSx } from '@/shared/components/confirmation/confirmationModalStyles'
import { RiCloseLine } from '@/shared/icons/remix'

interface Props {
  open: boolean
  onVerify: () => void
  onSkip: () => void
  isStarting: boolean
  /** False after abandon / attempt used — still show modal, but block starting a new session. */
  canStart?: boolean
}

/** Balanced verify prompt — roomier than compact, smaller than Figma handoff. */
const MODAL_WIDTH = 460
const PRIMARY = '#008CD0'
const TITLE = '#0D1B2A'
const BODY = '#62666F'
const CLOSE_BG = '#F4F7FA'

const primaryButtonSx = {
  textTransform: 'none' as const,
  width: '100%',
  bgcolor: PRIMARY,
  color: '#FFFFFF',
  borderRadius: '8px',
  height: 48,
  minHeight: 48,
  py: 0,
  fontFamily: '"Inter", sans-serif',
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '24px',
  boxShadow: '0px 6px 12px -3px #BAE6FD, 0px 2px 4px -2px #BAE6FD',
  '&:hover': {
    bgcolor: '#0077B3',
    boxShadow: '0px 6px 12px -3px #BAE6FD, 0px 2px 4px -2px #BAE6FD',
  },
  '&.Mui-disabled': { opacity: 0.7 },
}

export function EkycHomePrompt({
  open,
  onVerify,
  onSkip,
  isStarting,
  canStart = true,
}: Readonly<Props>) {
  return (
    <ConfirmationModalShell
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') onSkip()
      }}
      dialogTestId="ekyc-home-prompt"
      ariaLabelledBy="ekyc-home-title"
      ariaDescribedBy="ekyc-home-description"
      backdropSx={confirmationBackdropSx}
      paperSx={{
        width: { xs: 'calc(100% - 32px)', sm: MODAL_WIDTH },
        maxWidth: MODAL_WIDTH,
        borderRadius: '14px',
        boxShadow: '0px 8px 20px rgba(0, 0, 0, 0.18)',
        bgcolor: '#FFFFFF',
      }}
    >
      <Box sx={{ boxSizing: 'border-box', width: '100%', px: '24px', pt: '22px', pb: '22px' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: '12px',
          }}
        >
          <Typography
            id="ekyc-home-title"
            component="h2"
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 700,
              fontSize: '20px',
              lineHeight: '26px',
              color: TITLE,
              m: 0,
            }}
          >
            Verify Your Profile
          </Typography>
          <IconButton
            aria-label="Close"
            onClick={onSkip}
            disabled={isStarting}
            data-testid="ekyc-home-close"
            disableRipple
            sx={{
              width: 30,
              height: 30,
              borderRadius: '8px',
              bgcolor: CLOSE_BG,
              color: TITLE,
              p: 0,
              flexShrink: 0,
              '&:hover': { bgcolor: '#E8EEF3' },
            }}
          >
            <RiCloseLine size={20} aria-hidden />
          </IconButton>
        </Box>

        <Typography
          id="ekyc-home-description"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            color: BODY,
            m: 0,
            mb: '35px',
          }}
        >
          {canStart
            ? 'Verify your identity to unlock more survey opportunities and enjoy a safer, more secure experience.'
            : 'Your identity verification attempt has already been used. You can continue using Brushfire as normal.'}
        </Typography>

        {canStart ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
            <Button
              variant="contained"
              onClick={onVerify}
              disabled={isStarting}
              data-testid="ekyc-home-verify"
              sx={primaryButtonSx}
            >
              {isStarting ? <CircularProgress size={20} color="inherit" /> : 'Verify Now'}
            </Button>

            <Button
              variant="text"
              onClick={onSkip}
              disabled={isStarting}
              data-testid="ekyc-home-skip"
              sx={{
                textTransform: 'none',
                width: '100%',
                minHeight: 40,
                py: '8px',
                color: PRIMARY,
                fontFamily: '"Inter", sans-serif',
                fontWeight: 600,
                fontSize: '15px',
                lineHeight: '22px',
                bgcolor: 'transparent',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: 'rgba(0, 140, 208, 0.06)',
                  color: '#0077B3',
                  boxShadow: 'none',
                },
              }}
            >
              Remind Me Later
            </Button>
          </Box>
        ) : (
          <Button
            variant="contained"
            onClick={onSkip}
            data-testid="ekyc-home-skip"
            sx={{ ...primaryButtonSx, mt: 0 }}
          >
            Close
          </Button>
        )}
      </Box>
    </ConfirmationModalShell>
  )
}
